"use client";

import React from "react";
import { FlaskConical, ChartBar as BarChart3, Wrench, GraduationCap, Lightbulb, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface ApplicationArea {
  id: string;
  title: string;
  category: string;
  description: string;
  keyCapability: string;
  linkPath: string;
}
const applicationAreas: ApplicationArea[] = [{
  id: "app-1",
  title: "Lab Instrumentation",
  category: "Analytical Systems",
  description: "High-precision analytical instrumentation calibrated for compliance with pharmaceutical, chemical, and life science testing protocols.",
  keyCapability: "Qualification & Pharmacopeia standards compliance",
  linkPath: "/ProductsPage"
}, {
  id: "app-2",
  title: "Data Analysis Support",
  category: "Methodology",
  description: "End-to-end data processing workflows and statistical evaluation to interpret complex chemometric and chromatographic results.",
  keyCapability: "Audit-ready data integrity and method validation",
  linkPath: "/ServicesPage"
}, {
  id: "app-3",
  title: "Research Tools",
  category: "Laboratory Equipment",
  description: "Specialized apparatus, automated sampling modules, and accessories engineered to streamline daily experimental setups.",
  keyCapability: "Modular hardware compatibility and calibration",
  linkPath: "/SolutionsPage"
}, {
  id: "app-4",
  title: "Academic Collaboration",
  category: "Higher Education & Institutes",
  description: "Partnership programs supporting university research departments with customized equipment setups, training, and technical consultation.",
  keyCapability: "Multi-user lab frameworks and grant support",
  linkPath: "/AboutPage"
}, {
  id: "app-5",
  title: "Scientific Innovation",
  category: "Process Development",
  description: "Emerging analytical solutions for Process Analytical Technology (PAT), in-line process monitoring, and next-generation formulations.",
  keyCapability: "Continuous process optimization and testing",
  linkPath: "/ProcessAnalyticalTechnologyPage"
}];
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (id: string) => {
    switch (id) {
      case "app-1":
        return <FlaskConical className="size-6 text-primary" />;
      case "app-2":
        return <BarChart3 className="size-6 text-primary" />;
      case "app-3":
        return <Wrench className="size-6 text-primary" />;
      case "app-4":
        return <GraduationCap className="size-6 text-primary" />;
      case "app-5":
        return <Lightbulb className="size-6 text-primary" />;
      default:
        return <FlaskConical className="size-6 text-primary" />;
    }
  };
  return <section id="researchapplications" data-nav="light" className="bg-background py-20 md:py-28 border-b border-border">
      <div className="page-container">
        {/* Header Section */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium tracking-wide">
            Application-Led Technology
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Research Application Areas
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Specialized instrumentation, qualification frameworks, and scientific support engineered for regulated laboratories, academia, and manufacturing facilities across Ireland and the UK.
          </p>
        </div>

        {/* Applications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {applicationAreas.map((area, i) => <div key={area.id} data-index={i} className={`flex ${i === 4 ? "md:col-span-2 lg:col-span-1" : ""}`}>
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.08
          }} className="w-full flex">
                <Card className="flex-1 flex flex-col justify-between border-border bg-card hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 bg-secondary rounded-lg inline-flex items-center justify-center">
                        {getIcon(area.id)}
                      </div>
                      <Badge variant="outline" className="text-xs font-normal">
                        {area.category}
                      </Badge>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                        {area.title}
                      </CardTitle>
                      <CardDescription className="mt-2 text-sm leading-normal text-muted-foreground">
                        {area.description}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="pt-4 border-t border-border flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-xs text-muted-foreground font-medium">
                        {area.keyCapability}
                      </span>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between">
                      <Link to={area.linkPath}>
                        <span>Explore Focus Area</span>
                        <ArrowRight className="size-4 ml-2" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Bottom Banner */}
        <div className="mt-14 rounded-xl border border-border bg-secondary/50 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="text-lg font-default font-semibold text-foreground">
              Need custom research protocol support?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our technical specialists help identify, install, and qualify analytical equipment tailored to your specific methods.
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Button asChild variant="default" className="w-full md:w-auto">
              <Link to="/ContactPage">
                Contact Technical Team
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}